import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Tooltip, Box, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';

interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob) => void;
  onPlayback?: (audioUrl: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  showPlayback?: boolean;
  showMicIcon?: boolean;
  audioUrl?: string;
  onReplay?: () => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onPlayback,
  disabled = false,
  size = 'medium',
  showPlayback = false,
  showMicIcon = true,
  audioUrl,
  onReplay
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasRecording, setHasRecording] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const getIconSize = () => {
    switch (size) {
      case 'small': return 20;
      case 'large': return 32;
      default: return 24;
    }
  };

  const getButtonSize = () => {
    switch (size) {
      case 'small': return 'small';
      case 'large': return 'large';
      default: return 'medium';
    }
  };

  useEffect(() => {
    // Check if we're in an iframe
    setIsInIframe(window.self !== window.top);
    
    return () => {
      // Cleanup on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // Check if we're in an iframe and show helpful message
      if (window.self !== window.top) {
        console.log('🎤 Voice recording in iframe - microphone permission required');
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        onRecordingComplete(audioBlob);
        setHasRecording(true);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      
      // Provide more specific error messages
      const errorObj = error as Error;
      if (errorObj.name === 'NotAllowedError') {
        alert('Microphone access denied. Please allow microphone permissions and try again.');
      } else if (errorObj.name === 'NotFoundError') {
        alert('No microphone found. Please connect a microphone and try again.');
      } else if (errorObj.name === 'NotSupportedError') {
        alert('Microphone access not supported in this browser. Please use a modern browser.');
      } else if (window.self !== window.top) {
        alert('Microphone access blocked in iframe. Please allow microphone permissions for this site in your browser settings.');
      } else {
        alert('Unable to access microphone. Please check permissions and try again.');
      }
    }
  };


  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const playAudio = () => {
    if (audioUrl) {
      if (audioRef.current) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audio.onended = () => setIsPlaying(false);
        audio.onpause = () => setIsPlaying(false);
        audio.play();
        setIsPlaying(true);
      }
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const replayAudio = () => {
    if (onReplay) {
      onReplay();
    } else if (audioUrl) {
      playAudio();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {showMicIcon && !isRecording ? (
        <Tooltip title={
          isInIframe 
            ? "Start voice recording (microphone permission required)" 
            : hasRecording 
              ? "Record new message" 
              : "Start voice recording"
        }>
          <IconButton
            onClick={startRecording}
            disabled={disabled}
            size={getButtonSize()}
            sx={{
              color: disabled ? 'grey.400' : isInIframe ? 'warning.main' : 'primary.main',
              '&:hover': {
                backgroundColor: disabled ? 'transparent' : isInIframe ? 'warning.light' : 'primary.light',
                color: disabled ? 'grey.400' : isInIframe ? 'warning.dark' : 'primary.dark',
              }
            }}
          >
            <MicIcon sx={{ fontSize: getIconSize() }} />
          </IconButton>
        </Tooltip>
      ) : showMicIcon && isRecording ? (
        <Tooltip title="Stop recording">
          <IconButton
            onClick={stopRecording}
            size={getButtonSize()}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'error.dark',
              }
            }}
          >
            <StopIcon sx={{ fontSize: getIconSize() }} />
          </IconButton>
        </Tooltip>
      ) : null}

      {isRecording && (
        <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
          {formatTime(recordingTime)}
        </Typography>
      )}

      {showPlayback && audioUrl && (
        <>
          <Tooltip title={isPlaying ? "Pause audio" : "Play audio"}>
            <IconButton
              onClick={isPlaying ? pauseAudio : playAudio}
              size={getButtonSize()}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.dark',
                }
              }}
            >
              {isPlaying ? (
                <PauseIcon sx={{ fontSize: getIconSize() }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: getIconSize() }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Replay audio">
            <IconButton
              onClick={replayAudio}
              size={getButtonSize()}
              sx={{
                color: 'secondary.main',
                '&:hover': {
                  backgroundColor: 'secondary.light',
                  color: 'secondary.dark',
                }
              }}
            >
              <ReplayIcon sx={{ fontSize: getIconSize() }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default VoiceRecorder;
